import { AccessType } from "./enums/AccessType";
import {
  CompanyItem,
  PhoneNumber,
  Portfolio,
  PortfolioTabStatus,
  SignDocumentCount,
} from "@/common/common";
import { PaidSubscription } from "@/common/subscription";
import { CustomRoleForUser } from "@/common/customRole";
import { PortfolioCompany } from "@/stores/PortfolioStore";
import { DateValue } from "@/utils/Dates";
import { PortfolioOverviewDTO } from "@/portfolio/search/portfolioV2ApiClient";
import { PortfolioFeatureToggles } from "@/common/featureToggles";
import { EnterpriseAccountItem } from "@/portfolio/portfolioDomain";

export type StarredCompany = {
  organisationNumber: string;
  accessType: string;
};

export type AdminInCompany = {
  name: string;
  email: string;
};

export type AdditionalUserWithEmail = {
  company: CompanyItem;
  role: AccessType;
  email?: string;
  adminsInCompany: AdminInCompany[];
};

export type GetUserSettingsResponse = {
  starredCompanies?: StarredCompany[];
  preferredPortfolioTab?: PortfolioTabStatus;
  apiKeys?: ApiKey[];
  lastVisitedCompanies?: { [orgNo: string]: DateValue };
  additionalUsersWithEmail?: AdditionalUserWithEmail[];
};

export type ApiKey = {
  key: string;
  isActive: boolean;
  expiresAt: string;
};

export interface LoggedOnUserInfoDTO {
  user: User;
  userSettings: GetUserSettingsResponse;
  companies: PortfolioCompany[];
  subscriptionStatuses: PaidSubscription[];
  signDocumentCounts: SignDocumentCount[];
  portfolios: Portfolio[];
  userRoles: CustomRoleForUser[];
  requireSsoReauthentication?: boolean;
  requiredSsoProviders?: string[];
  requiredSsoAccountNames?: string[];
  portfolioOverview?: PortfolioOverviewDTO;
  portfolioFeatureToggles?: PortfolioFeatureToggles;
  enterpriseAccountItems?: EnterpriseAccountItem[];
}

export interface UserCompany {
  organisationNumber: string;
  name: string;
  isCustomer: boolean;
  accessType: AccessType;
  shareholderId?: number;
  onBehalfOfShareholderCompany?: string;
  onBehalfOfOrganisationNumber?: string;
  onBehalfOfShareholderPerson?: string;
}

export type Impersonation = {
  type: "ADMIN" | "USER";
  orgNo?: string;
  companyName?: string;
  reason?: ImpersonationReason;
  details?: string;
};

export type ImpersonationReason = "ONBOARDING_AND_SUPPORT" | "TECHNICAL_INVESTIGATION" | "OTHER";

export const impersonationReasonValues: ImpersonationReason[] = [
  "ONBOARDING_AND_SUPPORT",
  "TECHNICAL_INVESTIGATION",
  "OTHER",
];

export const impersonationReasonLabel = (reason: ImpersonationReason) => {
  switch (reason) {
    case "ONBOARDING_AND_SUPPORT":
      return { translated: "Onboard and support" };
    case "TECHNICAL_INVESTIGATION":
      return { translated: "Technical investigation" };
    case "OTHER":
      return { translated: "Other" };
  }
};

export interface User {
  userId: number;
  userName: string;
  email?: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  phoneNumber?: PhoneNumber;
  acceptedTerms: boolean;
  language: string;
  hasPassword: boolean;
  socialSecurityNumber?: string;
  authenticatedCountry?: string;
  impersonation?: Impersonation;
  isEmailVerified: boolean;
  externalId: string;
}

export const formatUserName = (user: User | null): string =>
  [user?.firstName, user?.middleName, user?.lastName].join(" ");

export type SsoLoginMethodType =
  | "BANKID_NORWAY"
  | "BANKID_SWEDEN"
  | "MITID_DENMARK"
  | "BANKID_FINLAND"
  | "BANKID"
  | "USERNAME_PASSWORD"
  | "MICROSOFT"
  | "GOOGLE";

export interface LoginMethodInfo {
  method: SsoLoginMethodType;
  lastUsed?: string;
}

export interface SsoAccountCandidate {
  externalId: string;
  name: string;
  email?: string;
  isEmailVerified: boolean;
  loginMethods: LoginMethodInfo[];
}

export interface SsoAccountSelection {
  provider: string;
  email: string;
  candidates: SsoAccountCandidate[];
}

export interface SsoConfirmResult {
  redirectTo: string | null;
}
